/** @jsx jsx */

import React, { Fragment as _, useContext } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/UI/Modal';
import ContinueBtn from 'components/lib/ContinueBtn/continueBtn_v2';
import EditableBtn from 'components/lib/EditableBtn';
import Image from 'components/lib/Image';
import EditableText from 'components/lib/EditableText';
import { colors } from 'styles/variables';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import renderBlock from 'services/apiHelpers/renderBlock';
import { InteractionContext } from '../../Layouts/ContainerLogical';

import Media from 'components/lib/Media';
import Video from 'components/lib/Video';
import SVG from 'components/lib/SVG';
import Embed from 'components/lib/Embed';

const { lPink, lGrey0 } = colors;
const maxWidth = 512;

const Feedback = ({
  id,
  block,
  correct,
  useAttemptFeedback = true,
  attempt,
  numberOfAttempts,
  showAttemptNumber = true,
  useMediaInFeedback = true,
  onClose,
  showCorrectAnswer,
}: {
  id?: any;
  block?: any;
  correct: boolean | string;
  useAttemptFeedback?: boolean;
  attempt?: number;
  numberOfAttempts?: number;
  showAttemptNumber?: boolean;
  useMediaInFeedback?: boolean;
  onClose?: any;
  showCorrectAnswer?: any;
}) => {
  const ctx = useContext(InteractionContext) as any;

  const Components = {
    Feedback: ({ children, ...props }) => children,
    Modal: ({ children, ...props }) => children,
    ModalWithEditableBtn: ({ children, ...props }) => children,
    Correct: ({ children, ...props }: { children?: any; props?: any }) =>
      children,
    Incorrect: ({ children, ...props }) => children,
    Attempt: ({ children, ...props }) => children,
    Image: ({ children, ...props }) => {
      return (
        <Image {...{ ...props, usePlaceholderValue: true, maxHeight: 30 }} />
      );
    },
    // Image,
    Video,
    Transcript: () => null,
    SVG,
    Embed,
    // Media: ({ children, ...props }) => {
    //   return (
    //     <Media {...{ remove: ['text', 'embed'], ...props }}>{children}</Media>
    //   );
    // },
    Media,
    Text: EditableText,
    EditableButton: ({ ...props }) => (
      <EditableBtn {...props} onClick={onClose} />
    ),
    // ContinueButton: ContinueBtn,
    ContinueButton: ({ ...props }) => (
      <ContinueBtn
        {...{
          ...props,
          unlocked: true,
          removeable: false,
          disableLastPageMessage: true,
        }}
        onClick={() => {}}
      />
    ),
  };

  const renderedBlock = renderBlock(
    block || findByKeyVal_V2(id, 'type', 'Feedback'),
    Components
  ) as any;

  const Feedbacks = renderedBlock.props.children;

  const Correct_Feedback = Feedbacks[0]; // renderedBlock.props.children[0];
  const Incorrect_Feedback = Feedbacks[1]; // renderedBlock.props.children[1];

  const Attempt_Feedback =
    Feedbacks[2].props.type === 'Attempt_Feedback'
      ? Feedbacks[2]
      : Feedbacks[1]; // renderedBlock.props.children[2];

  const FeedbackButtonCorrect = Correct_Feedback.props.children[2];
  const FeedbackButtonIncorrect = Incorrect_Feedback.props.children[2];
  const FeedbackButtonAttempt = Attempt_Feedback.props.children[2];

  // for backward compat. need to do a :
  // if attempt feedback doesn't exist ( old ). we use incorrect feedback

  const parsedOnClose = () => {
    if (window.dev()) {
      checkReturnToInteractionEditing(true);
      onClose();
      return;
    }

    onClose();

    if (correct === 'attempt') {
      return;
    }

    if (!correct && attempt < numberOfAttempts) {
      // console.log('WUT !?!?');
      return;
    }

    // if we're incorrect and showCorrectAnswer is on
    if (!correct && showCorrectAnswer === 1) {
      // we need to check if the button is using the ' default location ' ( next )
      // if so. user has understood they want to nav to somewhere else so we let them
      // even if they HAVE selected to showCorrectAnswer. that's a logicky fail
      // and we preference the navigation
      if (
        FeedbackButtonIncorrect &&
        FeedbackButtonIncorrect.props.location === 'next'
      ) {
        return;
      }
    }

    // don't run this until
    if (
      FeedbackButtonCorrect &&
      FeedbackButtonCorrect.props.location &&
      FeedbackButtonIncorrect &&
      FeedbackButtonIncorrect.props.location
    ) {
      ctx.next(
        correct
          ? FeedbackButtonCorrect.props.location
          : FeedbackButtonIncorrect.props.location
      );
    }
  };

  let img;
  let text;
  let button;
  let targetFeedbackElements;

  if (correct === 'attempt') {
    targetFeedbackElements = Attempt_Feedback.props.children;
  } else {
    targetFeedbackElements = Feedbacks[correct ? 0 : 1].props.children;
  }

  img = targetFeedbackElements[0];
  text = targetFeedbackElements[1];
  button = targetFeedbackElements[2];

  // reassign button
  if (correct !== 'attempt') {
    if (FeedbackButtonCorrect && FeedbackButtonIncorrect) {
      button = correct ? FeedbackButtonCorrect : FeedbackButtonIncorrect;
    } else {
      // for ' legacy ' feedback that won't have FeedbackButtonCorrect || FeedbackButtonIncorrect
      button = renderedBlock.props.children[2];
    }
  }

  if (!button) {
    button = renderedBlock.props.children[2];
  }

  const showAttemptNumberComputed =
    showAttemptNumber &&
    correct !== true &&
    numberOfAttempts > 1 &&
    attempt < numberOfAttempts;

  let nullSpaceCloseAction = () => {};

  if (button.props.location) {
    if (
      button.props.location === 'next' ||
      (numberOfAttempts > 1 && attempt < numberOfAttempts)
    ) {
      if (correct !== true) {
        nullSpaceCloseAction = onClose;
      }
    }
  } else {
    nullSpaceCloseAction = onClose;
  }

  // img = React.cloneElement(img, { remove: ['text', 'embed'] });

  return (
    // <Modal onClose={parsedOnClose}></Modal>
    <Modal onClose={window.dev() ? onClose : nullSpaceCloseAction}>
      <div
        css={css`
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          height: 100%;
          max-width: 768px;
          padding: 50px;
        `}
      >
        {useMediaInFeedback && (
          <div
            css={css`
            width: 75%;
            // max-width: ${maxWidth * 0.75}px;
            // max-height: 40vh;
            margin-bottom: 30px;

            img {
              max-height: 30vh !important;
            }

            @media (max-width: 768px) {
              width: 100%;
              margin-bottom: 25px;
            }
          `}
          >
            {/* {useAttemptFeedback
            ? img
            : renderedBlock.props.children[correct ? 0 : 1].props.children[0]} */}

            {img}
          </div>
        )}

        {showAttemptNumberComputed && (
          <div
            style={{
              margin: '0 0 15px 0',
              fontFamily: 'inherit',
              fontWeight: 700,
              color: '#333',
            }}
          >
            Attempt {attempt} of {numberOfAttempts}
          </div>
        )}
        <div
          css={css`
            width: 100%;
            margin-bottom: 30px;
          `}
        >
          {/* {useAttemptFeedback
            ? text
            : renderedBlock.props.children[correct ? 0 : 1].props.children[1]} */}
          {text}
        </div>
        <div
          css={css`
            display: flex;
            justify-self: flex-end;
            justify-content: center;
            // bottom: 50px;
            width: 100%;
          `}
        >
          <span
            css={css`
              cursor: pointer;
              &:hover {
                opacity: 0.8;
              }

              button {
                pointer-events: none;
              }
            `}
            onClick={e => {
              !window.dev() ? parsedOnClose() : null;
            }}
          >
            {/* {useAttemptFeedback
              ? button
              : FeedbackButtonCorrect && FeedbackButtonIncorrect
              ? correct
                ? FeedbackButtonCorrect
                : FeedbackButtonIncorrect
              : renderedBlock.props.children[2]} */}
            {button}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default Feedback;
